import { CalendarMonth, Edit } from "@mui/icons-material";
import { Grid, IconButton, ListItemText, Menu, MenuItem, Stack, TableHead } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import deleteAvailability from "api/availability/deleteAvailability";
import fetchAvailabilities from "api/availability/fetchAvailabilities";
import DataList from "components/DataList";
import ProfileAvailabilityEditorModal from "components/ProfileAvailabilityEditorModal";
import { useAppContextController } from "context/AppContext";
import useUpdateLocalQueryData from "hooks/useUpdateLocalQueryData";
import FormField from "layouts/applications/wizard/components/FormField";
import fetchEvents from "layouts/pages/events/actions/fetchEvents";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import AvailabilityRow from "./AvailabilityRow";

const ProfileAvailability = ({ currentUser }) => {
  const { venues, currentApplicant, userType, company, currentLoggedUser, companyType } =
    useAppContextController();
  const navigate = useNavigate();
  const [localStartDate, setLocalStartDate] = useState(moment().startOf("day").toDate());
  const [localEndDate, setLocalEndDate] = useState(moment().add(7, "days").endOf("day").toDate());
  const { enqueueSnackbar } = useSnackbar();
  const { deleteItemFromPaginatedLocal } = useUpdateLocalQueryData();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuEvent, setMenuEvent] = useState(null);
  const handleIconClick = (e, row) => {
    setAnchorEl(e.currentTarget);
    setMenuEvent(row);
  };
  const handleIconClose = () => {
    setAnchorEl(null);
    setMenuEvent(null);
  };

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [page, setPage] = useState(1);

  const options = useMemo(() => {
    return {
      fetchAll: false,
      orderBy: "startDate",
      order: "asc",
      limit: 10,
      filters: {
        userId: currentUser?._id,
      },
      startDate: localStartDate?.toISOString(),
      endDate: localEndDate?.toISOString(),
    };
  }, [currentUser?._id, localStartDate, localEndDate]);

  const key = [
    "availability",
    localStartDate?.toISOString(),
    localEndDate?.toISOString(),
    currentUser?._id,
  ];

  const {
    data: availability,
    isLoading,
    refetch,
    fetchNextPage,
  } = useInfiniteQuery(
    key,
    ({ queryKey, pageParam }) => fetchAvailabilities({ ...options, page: pageParam ?? 1 }),
    {
      enabled: !!localStartDate && !!localEndDate,
      getNextPageParam: (lastPage, pages) => {
        if (!lastPage?.data?.length || !lastPage?.pagination?.next?.page) {
          return undefined;
        }

        const nextPage = lastPage?.pagination?.next?.page;
        return nextPage ?? false;
      },
    }
  );

  const recruiterInterviews = useMemo(() => {
    return currentUser.interviews ?? [];
  }, [currentUser.interviews]);

  const optionsEvents = useMemo(() => {
    if (companyType === "Company") {
      return {
        fetchAll: true,
        filters: {
          status: "Active",
          eventUrl:
            recruiterInterviews
              .filter((int) => int.blocksAvailability === "Yes")
              .map((int) => int.eventUrl)
              .join(";") || "NONE",
        },
        startDate: localStartDate?.toISOString(),
        endDate: localEndDate?.toISOString(),
      };
    }

    return {
      fetchAll: true,
      filters: {
        eventType: "Screening Interview",
        status: "Active",
        "recruiter.userId": currentUser?._id,
      },
      startDate: localStartDate?.toISOString(),
      endDate: localEndDate?.toISOString(),
    };
  }, [companyType, currentUser?._id, localEndDate, localStartDate, recruiterInterviews]);

  const {
    data: interviewEvents,
    isLoading: eventsIsLoading = true,
    isFetching: eventsIsFetching,
  } = useQuery(["events", optionsEvents], () => fetchEvents(optionsEvents), {
    enabled: !!currentUser?._id,
    refetchOnWindowFocus: false,
  });

  const { mutate: deleteAvailabilityMutate } = useMutation(deleteAvailability, {
    onSuccess: (data, vars) => {
      enqueueSnackbar("Changes have been saved successfully", { variant: "success" });
      deleteItemFromPaginatedLocal(key, (item, idx) => item?._id === vars?.id);
      refetch();
    },
    onError: () => {
      enqueueSnackbar("An error has occurred deleting availability", { variant: "error" });
    },
  });

  const addNew = () => {
    setCreateModalOpen(true);
  };

  const handleDelete = (id) => {
    deleteAvailabilityMutate({ id });
  };

  const fullData = useMemo(
    () =>
      availability?.pages
        ?.flatMap((p) => {
          const currentItems = p?.data || [];
          return currentItems;
        })
        .map((av) => {
          if (!interviewEvents?.data?.length) {
            return av;
          }
          const conflictingEvents = interviewEvents.data.filter(
            (ev) => ev.eventDate <= av.endDate && ev.eventEndTime >= av.startDate
          );
          if (conflictingEvents?.length) {
            return {
              ...av,
              conflictingEvents,
            };
          }
          return av;
        }),
    [availability?.pages, interviewEvents?.data]
  );

  const header = (
    <>
      <Stack display="flex" flexDirection="row" justifyContent="right" pr={3}>
        <Grid>
          <IconButton variant="text" color="info" size="large" onClick={addNew}>
            <Edit />
          </IconButton>
        </Grid>
      </Stack>
    </>
  );

  const renderRow = (row) => (
    <AvailabilityRow row={row} onDelete={handleDelete} onViewEvents={handleIconClick} />
  );

  const topButtonBar = (
    <TableHead
      sx={{
        height: "5rem",
        zIndex: 3,
        position: "sticky",
        top: 0,
        background: "white",
        width: "100%",
      }}
      display="flex"
      width="100%"
    >
      <Grid container xs={12} justifyContent="space-between">
        <Grid item xs={4} />
        <Grid container item xs={6} spacing={2}>
          <Grid item sm={6}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Date From"
                views={["year", "month", "day"]}
                value={localStartDate}
                onChange={(time) => {
                  setLocalStartDate(moment(time).startOf("day").toDate());
                  if (moment(time).isAfter(moment(localEndDate))) {
                    setLocalEndDate(moment(time).endOf("day").toDate());
                  }
                }}
                renderInput={(params) => <FormField {...params} />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item sm={6}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Date To"
                views={["year", "month", "day"]}
                value={localEndDate}
                onChange={(time) => {
                  setLocalEndDate(moment(time).endOf("day").toDate());
                  if (moment(time).isBefore(moment(localStartDate))) {
                    setLocalStartDate(moment(time).startOf("day").toDate());
                  }
                }}
                renderInput={(params) => <FormField {...params} />}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Grid>
    </TableHead>
  );

  return (
    <>
      <DataList
        renderRow={renderRow}
        data={fullData}
        icon={<CalendarMonth />}
        iconColor="warning"
        title="Availability"
        isLoading={isLoading}
        topButtonBar={topButtonBar}
        greybar
        divider
        header={header}
        tableHeight={450}
        height={700}
        scrollY
        computedHeight
        loadMore={fetchNextPage}
        infiniteData={availability?.pages?.[availability?.pages?.length - 1]}
      />
      <ProfileAvailabilityEditorModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={refetch}
        currentUser={currentUser}
      />
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleIconClose}
        sx={{
          "& .MuiPaper-root": { minWidth: "10rem", p: "0.2rem" },
          li: { minWidth: "10rem", pl: "0.5rem", pr: "0.5rem" },
        }}
      >
        {menuEvent?.conflictingEvents?.map((ev) => (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleIconClose();
              navigate(`/events/${ev.eventUrl}/action/eventsinfo`);
            }}
          >
            <ListItemText
              primaryTypographyProps={{
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              {`${ev.eventName} (${moment(ev.eventDate).format("hh:mm A")} - ${moment(
                ev.eventEndTime
              ).format("hh:mm A")})`}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ProfileAvailability;
